// Optical character recognition, entirely in the browser, via Tesseract.js.
// Tesseract is loaded from a <script> tag in index.html and lives on window as
// a global, so we describe just the sliver of its API we use.

import { OCR_LANGS } from "../constants.js";

interface TesseractResult {
  data: { text: string };
}
interface TesseractLike {
  recognize(
    image: CanvasImageSource | HTMLCanvasElement | Blob | string,
    langs?: string,
    options?: { logger?: (msg: { status: string; progress: number }) => void },
  ): Promise<TesseractResult>;
}
declare global {
  interface Window {
    Tesseract?: TesseractLike;
  }
}

export function ocrAvailable(): boolean {
  return typeof window.Tesseract !== "undefined";
}

/**
 * Run OCR on an image source (canvas, blob or element).
 * `onProgress` gets a 0..1 fraction for the recognising phase.
 */
export async function runOcr(
  image: CanvasImageSource | HTMLCanvasElement | Blob | string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const tesseract = window.Tesseract;
  if (!tesseract) {
    throw new Error(
      "OCR engine failed to load. Check your connection and reload.",
    );
  }
  const result = await tesseract.recognize(image, OCR_LANGS, {
    logger: (msg) => {
      if (msg.status === "recognizing text" && onProgress) {
        onProgress(msg.progress);
      }
    },
  });
  return result.data.text ?? "";
}
